import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  CardContent,
  Card,
  List,
  Button,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Chip,
  Paper,
  Tooltip,
  Stack,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FindReplaceIcon from "@mui/icons-material/FindReplace";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MapIcon from "@mui/icons-material/Map";
import LayersIcon from "@mui/icons-material/Layers";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { useNavigate } from "react-router-dom";
import {
  Group,
  Image as KonvaImage,
  Layer,
  Line,
  Rect,
  Stage,
} from "react-konva";
import { toast } from "react-toastify";
import FpCropEdit, { MAP_IMAGE } from "./FpCropEditor";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import CustomCircularP from "../../widgets/CustomCircularP";
import BackBtn from "../../widgets/BackBtn";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import RdMyModal from "../../widgets/myModal/RdMyModal";
import { getHoverColor, hexToRGBA, toastError } from "../../utils/util";
import useApiEp from "../../hooks/useApiEp";
import { getHeader } from "../../utils/utils2";

const TRANSPARENCY = 0.8;

export function getImageDimen(imgWidth, imgHeight) {
  const width = Math.min(imgWidth, 1280);
  const height = width * (imgHeight / imgWidth);
  return { width, height };
}

export function getScales(containerWidth, imgWidth, imgHeight) {
  const scaleX =
    Math.min(imgWidth, containerWidth) /
    getImageDimen(imgWidth, imgHeight).width;
  const scaleY =
    (Math.min(imgWidth, containerWidth) * (imgHeight / imgWidth)) /
    getImageDimen(imgWidth, imgHeight).height;

  return { scaleX, scaleY };
}

function GeneralFloorPlan() {
  const [reloadFp, setReloadFp] = useState(false);
  const { data, isLoading, isError, error } = useApiEp(
    `/admin/get-floor?id=1&r=${reloadFp}`
  );
  const navigate = useNavigate();
  const [showImageCrop, setShowImageCrop] = useState(false);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [image, setImage] = useState(null);
  const [gMenuEl, setGmenuEl] = useState(null);
  const [hMenuEl, setHmenuEl] = useState(null);
  const [stageScale, setStageScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [newHalls, setNewHalls] = useState([]);
  const [showHallDelete, setShowHallDelete] = useState(false);
  const [showFloorDelete, setShowFloorDelete] = useState(false);
  const [targetHall, setTargetHall] = useState();
  const [hallDeleting, setHallDeleting] = useState(false);
  const [floorDeleting, setFloorDeleting] = useState(false);
  const [confirmAllDelete, setConfirmAllDelete] = useState(false);

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.08;
    const stage = e.target.getStage();
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const newScale = e.evt.deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    const minScale = 0.5;
    const maxScale = 3;
    const finalScale = Math.max(minScale, Math.min(maxScale, newScale));

    setStageScale(finalScale);

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newPos = {
      x: pointer.x - mousePointTo.x * finalScale,
      y: pointer.y - mousePointTo.y * finalScale,
    };

    setStagePos(newPos);
  };

  const handleZoomIn = () => {
    setStageScale((prev) => Math.min(3, prev * 1.2));
  };

  const handleZoomOut = () => {
    setStageScale((prev) => Math.max(0.5, prev / 1.2));
  };

  const handleResetZoom = () => {
    setStageScale(1);
    setStagePos({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (data && !image) {
      const img = new window.Image();
      img.src = data.image;
      img.onload = () => {
        setImage({
          img: img,
          width: img.width,
          height: img.height,
        });
      };
    }
  }, [data, image]);

  useEffect(() => {
    if (data && image && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      setDimensions({
        width: containerWidth,
        height: 580,
      });
      const scale = getScales(containerWidth, image.width, image.height);
      const scaleX = scale.scaleX;
      const scaleY = scale.scaleY;

      const halls = data.halls.map((hall) => {
        if (hall.shape.type === "Rectangle") {
          return {
            ...hall,
            shape: {
              ...hall.shape,
              shape: {
                ...hall.shape.shape,
                x: hall.shape.shape.x * scaleX,
                y: hall.shape.shape.y * scaleY,
                width: hall.shape.shape.width * scaleX,
                height: hall.shape.shape.height * scaleY,
              },
            },
          };
        } else if (hall.shape.type === "Polygon") {
          return {
            ...hall,
            shape: {
              ...hall.shape,
              shape: hall.shape.shape.map((point, index) =>
                index % 2 === 0 ? point * scaleX : point * scaleY
              ),
            },
          };
        }
        return hall;
      });
      setNewHalls(halls);
    }
  }, [data, image, containerRef]);

  const hanldeHallMenuClick = (e, hall) => {
    setTargetHall(hall);
    setHmenuEl(e.currentTarget);
  };

  const handleGeneralPlanEdit = (e) => {
    setGmenuEl(e.currentTarget);
  };

  const handleGmenuClick = (event, action) => {
    setGmenuEl(null);
    switch (action) {
      case "Edit Image":
      case "Replace Image":
        setShowImageCrop(true);
        break;
      case "Delete Image":
        setShowFloorDelete(true);
        break;
      default:
        console.warn("Unknown action:", action);
        break;
    }
  };

  const handleHmenuClick = (e, m) => {
    setHmenuEl(null);
    switch (m) {
      case "Edit Hall":
        navigate(`/fp-add-hall?id=${targetHall._id}`);
        break;
      case "Delete Hall":
        setShowHallDelete(true);
        break;
      default:
        console.warn("Unknown action:", m);
        break;
    }
  };

  const handleHallDelete = async (id) => {
    setHallDeleting(true);
    try {
      const response = await axios.delete(
        `${API_ENDPOINT}/admin/delete-hall?id=${id}`,
        getHeader()
      );
      setReloadFp((prev) => !prev);
      toast.success(response.data.message);
    } catch (err) {
      toastError(err);
    } finally {
      setHallDeleting(false);
    }
  };

  const handleFloorDelete = async (id) => {
    setFloorDeleting(true);
    try {
      const { data } = await axios.delete(
        `${API_ENDPOINT}/user/delete-floor?id=${id}`,
        getHeader()
      );
      toast.success(data.message);
      setTimeout(() => {
        document.location.reload();
      }, 500);
    } catch (err) {
      toastError(err);
    } finally {
      setFloorDeleting(false);
    }
  };

  const onYesHallDelete = () => {
    setShowHallDelete(false);
    handleHallDelete(targetHall._id);
  };

  const onYesFloorDelete = () => {
    setShowFloorDelete(false);
    handleFloorDelete(data?._id);
  };

  const onYesDeleteAllHalls = async () => {
    setHallDeleting(true);
    try {
      const { data: resData } = await axios.delete(
        `${API_ENDPOINT}/user/delete-all-halls?id=${data?.eventId}`,
        getHeader(ADMIN)
      );
      toast.success(resData.message);
      setTimeout(() => {
        document.location.reload();
      }, 500);
    } catch (err) {
      toastError(err);
    } finally {
      setHallDeleting(false);
    }
    setConfirmAllDelete(false);
  };

  return (
    <Box sx={{ maxWidth: "1340px", mx: "auto", px: { xs: 2, md: 3 }, pb: 5 }}>
      <CustomCircularP show={isLoading || hallDeleting} />
      <RdMyModal
        showModal={showImageCrop}
        setShowModal={setShowImageCrop}
        modalC={<FpCropEdit upload_type={"MAP_IMAGE"} />}
      />

      <SimpleYesNoDialog
        title="Please Confirm!"
        message="Are you sure you want to delete all halls?"
        onYes={onYesDeleteAllHalls}
        show={confirmAllDelete}
        setShow={setConfirmAllDelete}
      />
      <SimpleYesNoDialog
        title="Please Confirm!"
        message="Are you sure you want to delete this Map?"
        onYes={onYesFloorDelete}
        show={showFloorDelete}
        setShow={setShowFloorDelete}
      />
      <SimpleYesNoDialog
        title="Please Confirm!"
        message={`Are you sure you want to delete ${targetHall?.name}? All data related to this hall will be deleted.`}
        onYes={onYesHallDelete}
        show={showHallDelete}
        setShow={setShowHallDelete}
      />

      <BackBtn />

      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: "#0F172A", letterSpacing: "-0.02em" }}
          >
            Floor Plan Creator
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748B", mt: 0.5 }}>
            Upload event floor plan maps and define interactive hall boundaries.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
          <Button
            variant="outlined"
            startIcon={<FindReplaceIcon />}
            disabled={!data?.image}
            onClick={() => setShowImageCrop(true)}
            sx={{
              borderRadius: "10px",
              borderColor: "#E2E8F0",
              color: "#475467",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#FFFFFF",
              "&:hover": { bgcolor: "#F8FAFC" },
            }}
          >
            Replace Map
          </Button>

          <Button
            variant="contained"
            disableElevation
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate("/fp-add-hall", { replace: true })}
            sx={{
              borderRadius: "10px",
              bgcolor: "#2563EB",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            Add New Hall
          </Button>
        </Stack>
      </Box>

      {/* Action Menus */}
      <Menu
        anchorEl={gMenuEl}
        open={Boolean(gMenuEl)}
        onClose={() => setGmenuEl(null)}
        PaperProps={{ elevation: 2, sx: { borderRadius: "10px", minWidth: 160 } }}
      >
        <MenuItem
          disabled={!image}
          onClick={(e) => handleGmenuClick(e, "Replace Image")}
        >
          <ListItemIcon>
            <FindReplaceIcon fontSize="small" />
          </ListItemIcon>
          Replace Image
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={!image}
          sx={{ color: "#DC2626" }}
          onClick={(e) => handleGmenuClick(e, "Delete Image")}
        >
          <ListItemIcon>
            <DeleteOutlineIcon sx={{ color: "#DC2626" }} fontSize="small" />
          </ListItemIcon>
          Delete Image
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={hMenuEl}
        open={Boolean(hMenuEl)}
        onClose={() => setHmenuEl(null)}
        PaperProps={{ elevation: 2, sx: { borderRadius: "10px", minWidth: 150 } }}
      >
        <MenuItem onClick={(e) => handleHmenuClick(e, "Edit Hall")}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit Hall
        </MenuItem>
        <Divider />
        <MenuItem
          sx={{ color: "#DC2626" }}
          onClick={(e) => handleHmenuClick(e, "Delete Hall")}
        >
          <ListItemIcon>
            <DeleteOutlineIcon sx={{ color: "#DC2626" }} fontSize="small" />
          </ListItemIcon>
          Delete Hall
        </MenuItem>
      </Menu>

      <Grid container spacing={3}>
        {/* Interactive Stage Canvas Section */}
        <Grid item xs={12} lg={8}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 4px 20px rgba(15, 23, 42, 0.05)",
              bgcolor: "#FFFFFF",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2,
                px: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #F1F5F9",
                bgcolor: "#F8FAFC",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <MapIcon sx={{ color: "#2563EB" }} />
                <Typography variant="h6" fontWeight={700} color="#0F172A">
                  General Floor Plan Canvas
                </Typography>
                {image && (
                  <Chip
                    label={`${Math.round(stageScale * 100)}% Zoom`}
                    size="small"
                    sx={{
                      bgcolor: "#EFF6FF",
                      color: "#2563EB",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  />
                )}
              </Stack>

              {data?.image && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Zoom In">
                    <IconButton size="small" onClick={handleZoomIn} sx={{ bgcolor: "#FFFFFF", border: "1px solid #E2E8F0" }}>
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Zoom Out">
                    <IconButton size="small" onClick={handleZoomOut} sx={{ bgcolor: "#FFFFFF", border: "1px solid #E2E8F0" }}>
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reset View">
                    <IconButton size="small" onClick={handleResetZoom} sx={{ bgcolor: "#FFFFFF", border: "1px solid #E2E8F0" }}>
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton onClick={handleGeneralPlanEdit} color="primary">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Box>

            <CardContent sx={{ p: 2, position: "relative" }}>
              {data?.image ? (
                <Box
                  ref={containerRef}
                  sx={{
                    overflow: "hidden",
                    position: "relative",
                    borderRadius: "12px",
                    border: "1px solid #E2E8F0",
                    bgcolor: "#0F172A",
                    cursor: "grab",
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                  {image ? (
                    <Stage
                      width={dimensions.width}
                      height={dimensions.height}
                      scaleX={stageScale}
                      scaleY={stageScale}
                      x={stagePos.x}
                      y={stagePos.y}
                      draggable={true}
                      onWheel={handleWheel}
                    >
                      <Layer>
                        <KonvaImage
                          image={image.img}
                          width={Math.min(image.width, dimensions.width)}
                          height={
                            Math.min(image.width, dimensions.width) *
                            (image.height / image.width)
                          }
                          x={0}
                          y={0}
                        />
                        {newHalls
                          ?.filter((hall) => hall.shape.type === "Polygon")
                          .map((hall, index) => (
                            <Group key={index}>
                              <Line
                                points={hall.shape.shape}
                                fill={hexToRGBA(hall.color, TRANSPARENCY)}
                                stroke={hexToRGBA(hall.color, TRANSPARENCY)}
                                strokeWidth={hall.strokeWidth}
                                closed={true}
                                onClick={() => {
                                  navigate(`/hall-plan/${hall._id}`);
                                }}
                                onMouseEnter={(e) => {
                                  const shape = e.target;
                                  shape.fill(getHoverColor(hall.color));
                                  shape.getLayer().batchDraw();
                                }}
                                onMouseLeave={(e) => {
                                  const shape = e.target;
                                  shape.fill(
                                    hexToRGBA(hall.color, TRANSPARENCY)
                                  );
                                  shape.getLayer().batchDraw();
                                }}
                              />
                            </Group>
                          ))}
                        {newHalls
                          ?.filter((hall) => hall.shape.type === "Rectangle")
                          .map((hall, index) => (
                            <Rect
                              key={index}
                              x={hall.shape.shape.x}
                              y={hall.shape.shape.y}
                              width={hall.shape.shape.width}
                              height={hall.shape.shape.height}
                              fill={hexToRGBA(hall.color, TRANSPARENCY)}
                              onClick={() => {
                                navigate(`/hall-plan/${hall._id}`);
                              }}
                              onMouseEnter={(e) => {
                                const shape = e.target;
                                shape.fill(getHoverColor(hall.color));
                                shape.getLayer().batchDraw();
                              }}
                              onMouseLeave={(e) => {
                                const shape = e.target;
                                shape.fill(hexToRGBA(hall.color, TRANSPARENCY));
                                shape.getLayer().batchDraw();
                              }}
                            />
                          ))}
                      </Layer>
                    </Stage>
                  ) : (
                    <Box
                      sx={{
                        p: 5,
                        width: "100%",
                        height: 560,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#F8FAFC",
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        Loading Map Image Canvas...
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  onClick={() => setShowImageCrop(true)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "460px",
                    border: "2px dashed #2563EB",
                    borderRadius: "14px",
                    bgcolor: "#F0F6FF",
                    p: 4,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "#EFF6FF",
                      borderColor: "#1D4ED8",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      bgcolor: "#DBEAFE",
                      color: "#2563EB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 32 }} />
                  </Box>
                  <Typography variant="h6" color="#0F172A" fontWeight={700}>
                    Click to Upload Floor Plan Map
                  </Typography>
                  <Typography variant="body2" color="#64748B" sx={{ mt: 0.5, textAlign: "center" }}>
                    Supported formats: JPG, JPEG, PNG (max 2MB file size).
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Exhibition Halls List Sidebar Panel */}
        <Grid item xs={12} lg={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 4px 20px rgba(15, 23, 42, 0.05)",
              bgcolor: "#FFFFFF",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                p: 2.5,
                pb: 2,
                borderBottom: "1px solid #F1F5F9",
                bgcolor: "#F8FAFC",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LayersIcon sx={{ color: "#7C3AED" }} />
                  <Typography variant="h6" fontWeight={700} color="#0F172A">
                    Hall Directory
                  </Typography>
                </Stack>
                <Chip
                  label={`${data?.halls?.length || 0} Halls`}
                  size="small"
                  sx={{
                    bgcolor: "#F5F3FF",
                    color: "#7C3AED",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={1} mt={2}>
                <LoadingButton
                  sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, flex: 1 }}
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={!data?.halls || data.halls.length === 0}
                  onClick={() => setConfirmAllDelete(true)}
                >
                  Clear All
                </LoadingButton>

                <Button
                  sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, flex: 1 }}
                  color="primary"
                  variant="contained"
                  disableElevation
                  startIcon={<AddCircleOutlineIcon fontSize="small" />}
                  size="small"
                  onClick={() => navigate("/fp-add-hall", { replace: true })}
                >
                  Add Hall
                </Button>
              </Stack>
            </Box>

            <CardContent sx={{ p: 2.5, flexGrow: 1, overflowY: "auto" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#94A3B8", letterSpacing: "0.05em", mb: 1.5, display: "block" }}>
                EXHIBITION HALL BOUNDARIES
              </Typography>

              <Stack spacing={1.5}>
                {data && data.halls.length > 0 ? (
                  data.halls.map((hall, index) => (
                    <Paper
                      key={hall._id || index}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        border: "1px solid #E2E8F0",
                        bgcolor: "#FFFFFF",
                        transition: "all 0.2s",
                        position: "relative",
                        overflow: "hidden",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
                          borderColor: "#CBD5E1",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          bgcolor: hall.color || "#2563EB",
                        }}
                      />

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ pl: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700} color="#0F172A">
                            {hall.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Shape: {hall.shape?.type || "Custom Polygon"}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => navigate(`/hall-plan/${hall._id}`)}
                            endIcon={<ArrowForwardIcon style={{ fontSize: 14 }} />}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              color: "#2563EB",
                              fontSize: "0.8rem",
                            }}
                          >
                            Open Layout
                          </Button>
                          <IconButton
                            onClick={(e) => hanldeHallMenuClick(e, hall)}
                            size="small"
                            sx={{ color: "#64748B" }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: "12px",
                      bgcolor: "#F8FAFC",
                      border: "1px dashed #CBD5E1",
                    }}
                  >
                    <Typography variant="body1" fontWeight={600} color="text.secondary">
                      No halls mapped yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Click 'Add Hall' above to draw hall boundaries on your map.
                    </Typography>
                  </Paper>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default GeneralFloorPlan;
